// src/pages/Messaging.tsx
import React, { useState } from 'react';
import {
  Box,
  Typography,
  useTheme,
  useMediaQuery,
  IconButton,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ChatIcon from '@mui/icons-material/Chat';
import { useMessaging } from '../contexts/MessagingContext';
import { useTranslation } from 'react-i18next';
import ConversationList from '../components/messaging/ConversationList';
import ChatPanel from '../components/messaging/ChatPanel';
import NewConversationDialog from '../components/messaging/NewConversationDialog';
import CreateGroupDialog from '../components/messaging/CreateGroupDialog';

const Messaging: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { state, selectConversation, selectGroup } = useMessaging();
  const { currentConversation, currentGroup, activeTab } = state;

  const [newConversationDialogOpen, setNewConversationDialogOpen] = useState(false);
  const [createGroupDialogOpen, setCreateGroupDialogOpen] = useState(false);

  // Determine if we should show the chat panel (on mobile, only when a chat is selected)
  const hasActiveChat = currentConversation || currentGroup;
  const showChatPanel = !isMobile || hasActiveChat;
  const showConversationList = !isMobile || !hasActiveChat;

  // Handle back button on mobile
  const handleBack = () => {
    if (activeTab === 'private') {
      selectConversation(null);
    } else {
      selectGroup(null);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        height: 'calc(100vh - 64px)', // Full height minus AppBar
        overflow: 'hidden',
        backgroundColor: 'background.default',
      }}
    >
      {/* Conversation List */}
      {showConversationList && (
        <ConversationList
          onNewConversation={() => setNewConversationDialogOpen(true)}
          onNewGroup={() => setCreateGroupDialogOpen(true)}
        />
      )}

      {/* Chat Panel */}
      {showChatPanel && (
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: 'background.paper',
            position: 'relative',
          }}
        >
          {hasActiveChat ? (
            <>
              {/* Back button for mobile */}
              {isMobile && (
                <IconButton
                  onClick={handleBack}
                  sx={{
                    position: 'absolute',
                    top: 12,
                    left: 8,
                    zIndex: 10,
                  }}
                >
                  <ArrowBackIcon />
                </IconButton>
              )}
              <ChatPanel
                conversation={currentConversation}
                group={currentGroup}
              />
            </>
          ) : (
            // Empty state when no chat is selected (desktop only)
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'text.secondary',
                p: 4,
              }}
            >
              <Box
                sx={{
                  width: 120,
                  height: 120,
                  borderRadius: '50%',
                  backgroundColor: (theme) => theme.palette.grey[100],
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 3,
                }}
              >
                <ChatIcon sx={{ fontSize: 48, color: 'grey.400' }} />
              </Box>
              <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                {t('messaging.selectConversation', 'Sélectionnez une conversation')}
              </Typography>
              <Typography variant="body2" sx={{ textAlign: 'center', maxWidth: 300 }}>
                {t(
                  'messaging.selectConversationHint',
                  'Choisissez une conversation dans la liste ou démarrez une nouvelle discussion'
                )}
              </Typography>
            </Box>
          )}
        </Box>
      )}

      {/* Dialogs */}
      <NewConversationDialog
        open={newConversationDialogOpen}
        onClose={() => setNewConversationDialogOpen(false)}
      />
      <CreateGroupDialog
        open={createGroupDialogOpen}
        onClose={() => setCreateGroupDialogOpen(false)}
      />
    </Box>
  );
};

export default Messaging;
